import type { Tool } from "./clay.ts";

/**
 * The game's sounds, all made on the spot with the Web Audio API rather than recorded: the clay
 * under each tool, the kiln's fire and bell, the cat-face iris, the shop's stamp and the slip's
 * sparkle, the kitten's mews and purr, a jingle for each rank, a tap for every button, and the
 * workshop's music.
 * Nothing sounds until the player starts from the start page (browsers keep a page silent until
 * it is touched); while the page is hidden, everything is paused.
 */

/** Where a voice sounds: into the effects or the music, each partly through the room's echo. */
interface Mix {
  ctx: BaseAudioContext;
  effects: GainNode;
  music: GainNode;
  /** White noise, the stuff of hisses, fire and purrs. */
  noise: AudioBuffer;
  /** A fire's crackle: short snaps now and then, on silence. */
  crackle: AudioBuffer;
}

/** How much of the effects and of the music goes into the room's echo. */
const ECHO = { effects: 0.12, music: 0.45 };

function mixOn(ctx: BaseAudioContext): Mix {
  // Whatever piles up is held under full scale.
  const limiter = new DynamicsCompressorNode(ctx, {
    threshold: -6,
    knee: 4,
    ratio: 10,
    attack: 0.003,
    release: 0.2,
  });
  limiter.connect(ctx.destination);
  const echo = new ConvolverNode(ctx, { buffer: roomOf(ctx) });
  echo.connect(limiter);
  const bus = (send: number) => {
    const gain = new GainNode(ctx, { gain: 0 });
    gain.connect(limiter);
    gain.connect(new GainNode(ctx, { gain: send })).connect(echo);
    return gain;
  };
  const rate = ctx.sampleRate;
  const noise = new AudioBuffer({ length: rate * 2, sampleRate: rate });
  const data = noise.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  const crackle = new AudioBuffer({ length: rate * 3, sampleRate: rate });
  const snaps = crackle.getChannelData(0);
  for (let at = 0; at < snaps.length; at += Math.round(rate * (0.02 + Math.random() * 0.13))) {
    const loud = 0.2 + Math.random() * 0.8,
      length = Math.round(rate * (0.002 + Math.random() * 0.012));
    for (let i = 0; i < length && at + i < snaps.length; i++) {
      snaps[at + i] = (Math.random() * 2 - 1) * loud * (1 - i / length) ** 2;
    }
  }
  return { ctx, effects: bus(ECHO.effects), music: bus(ECHO.music), noise, crackle };
}

/** A small wooden room's echo: noise dying away, its highs sooner than its lows. */
function roomOf(ctx: BaseAudioContext): AudioBuffer {
  const length = Math.round(ctx.sampleRate * 1.8);
  const room = new AudioBuffer({ numberOfChannels: 2, length, sampleRate: ctx.sampleRate });
  for (let c = 0; c < 2; c++) {
    const data = room.getChannelData(c);
    let dull = 0;
    for (let i = 0; i < length; i++) {
      const age = i / length;
      dull += (Math.random() * 2 - 1 - dull) * (0.9 - 0.75 * age);
      data[i] = dull * (1 - age) ** 3;
    }
  }
  return room;
}

// ---------- The makings of every sound ----------

/** A gain rising to `peak` in `attack` seconds from `t`, then dying away over `decay`. */
function envelope(m: Mix, t: number, peak: number, attack: number, decay: number): GainNode {
  const g = new GainNode(m.ctx, { gain: 0 });
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(peak, t + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);
  return g;
}

interface Note {
  type?: OscillatorType;
  /** Pitch at the start (Hz), and where it glides to over `glide` seconds (by default, all). */
  from: number;
  to?: number;
  glide?: number;
  peak: number;
  attack?: number;
  decay: number;
  /** A lowpass over it (Hz). */
  cut?: number;
  out?: AudioNode;
}

/** One oscillator's note. */
function tone(m: Mix, t: number, n: Note): void {
  const { ctx } = m, attack = n.attack ?? 0.005, end = t + attack + n.decay;
  const osc = new OscillatorNode(ctx, { type: n.type ?? "sine", frequency: n.from });
  osc.frequency.setValueAtTime(n.from, t);
  if (n.to) osc.frequency.exponentialRampToValueAtTime(n.to, t + (n.glide ?? attack + n.decay));
  let node: AudioNode = osc;
  if (n.cut) node = node.connect(new BiquadFilterNode(ctx, { type: "lowpass", frequency: n.cut }));
  node.connect(envelope(m, t, n.peak, attack, n.decay)).connect(n.out ?? m.effects);
  osc.start(t);
  osc.stop(end + 0.05);
}

interface Hiss {
  filter: BiquadFilterType;
  /** The filter's frequency at the start (Hz), and where it sweeps to by the end. */
  from: number;
  to?: number;
  q?: number;
  peak: number;
  attack?: number;
  decay: number;
  out?: AudioNode;
}

/** A burst of filtered noise. */
function hiss(m: Mix, t: number, h: Hiss): void {
  const { ctx } = m, attack = h.attack ?? 0.005, end = t + attack + h.decay;
  const source = new AudioBufferSourceNode(ctx, { buffer: m.noise, loop: true });
  const filter = new BiquadFilterNode(ctx, { type: h.filter, frequency: h.from, Q: h.q ?? 1 });
  filter.frequency.setValueAtTime(h.from, t);
  if (h.to) filter.frequency.exponentialRampToValueAtTime(h.to, end);
  source.connect(filter).connect(envelope(m, t, h.peak, attack, h.decay)).connect(
    h.out ?? m.effects,
  );
  source.start(t, Math.random() * m.noise.duration);
  source.stop(end + 0.05);
}

/** `x`, a little off each time: no two squishes quite alike. */
const vary = (x: number, by = 0.08) => x * (1 + (Math.random() * 2 - 1) * by);
const hz = (midi: number) => 440 * 2 ** ((midi - 69) / 12);
const pick = <T>(xs: T[]) => xs[Math.floor(Math.random() * xs.length)];

// ---------- The clay ----------

/** The clay under each tool, `level` loud: pressed on, or (softer) worked as the brush moves. */
function squish(m: Mix, t: number, tool: Tool, level: number): void {
  const k = vary(1);
  if (tool === "grab") {
    // むにゅっ: drawn out, rising.
    tone(m, t, {
      type: "triangle",
      from: 150 * k,
      to: 280 * k,
      peak: 0.4 * level,
      attack: 0.01,
      decay: 0.2,
      cut: 900,
    });
    hiss(m, t, {
      filter: "bandpass",
      from: 900 * k,
      to: 1700 * k,
      q: 4,
      peak: 0.1 * level,
      attack: 0.01,
      decay: 0.14,
    });
  } else if (tool === "push") {
    // むにっ: pressed in, sinking.
    tone(m, t, { from: 210 * k, to: 90 * k, glide: 0.1, peak: 0.55 * level, decay: 0.16 });
    hiss(m, t, {
      filter: "bandpass",
      from: 1400 * k,
      to: 500 * k,
      q: 3,
      peak: 0.12 * level,
      decay: 0.1,
    });
  } else if (tool === "inflate") {
    // ぷくっ: swelling, as a bubble rises.
    tone(m, t, { from: 190 * k, to: 460 * k, glide: 0.07, peak: 0.45 * level, decay: 0.1 });
  } else if (tool === "smooth") {
    // すりっ: stroked over.
    hiss(m, t, {
      filter: "bandpass",
      from: 2400 * k,
      to: 1500 * k,
      q: 1.2,
      peak: 0.5 * level,
      attack: 0.03,
      decay: 0.18,
    });
  } else {
    // ぺたっ: patted flat.
    hiss(m, t, {
      filter: "lowpass",
      from: 2200 * k,
      to: 700 * k,
      peak: 0.4 * level,
      attack: 0.002,
      decay: 0.06,
    });
    tone(m, t, {
      from: 160 * k,
      to: 70 * k,
      glide: 0.06,
      peak: 0.45 * level,
      attack: 0.003,
      decay: 0.09,
    });
  }
}

/** ぽむ: the paw print pressed on as a stroke is counted. */
function paw(m: Mix, t: number): void {
  tone(m, t, { from: 640, to: 430, glide: 0.05, peak: 0.16, attack: 0.004, decay: 0.08 });
}

/** しゅるっ: a stroke taken back. */
function rewind(m: Mix, t: number): void {
  tone(m, t, {
    type: "triangle",
    from: 720,
    to: 360,
    glide: 0.12,
    peak: 0.14,
    attack: 0.01,
    decay: 0.13,
  });
  hiss(m, t, {
    filter: "bandpass",
    from: 3200,
    to: 1200,
    q: 2,
    peak: 0.08,
    attack: 0.02,
    decay: 0.12,
  });
}

/** ぽよん: back to the starting clay. */
function restart(m: Mix, t: number): void {
  tone(m, t, { from: 300, to: 200, glide: 0.08, peak: 0.35, decay: 0.12 });
  tone(m, t + 0.09, { from: 440, to: 260, glide: 0.2, peak: 0.3, decay: 0.26 });
}

/** ぷるるん: the clay wobbling as the morph arrives. */
function wobble(m: Mix, t: number): void {
  const { ctx } = m;
  const osc = new OscillatorNode(ctx, { frequency: 300 });
  const lfo = new OscillatorNode(ctx, { frequency: 13 });
  const depth = new GainNode(ctx, { gain: 70 });
  depth.gain.setValueAtTime(70, t);
  depth.gain.exponentialRampToValueAtTime(1, t + 0.5);
  lfo.connect(depth).connect(osc.frequency);
  osc.connect(new BiquadFilterNode(ctx, { type: "lowpass", frequency: 1200 }))
    .connect(envelope(m, t, 0.22, 0.01, 0.5))
    .connect(m.effects);
  for (const o of [osc, lfo]) {
    o.start(t);
    o.stop(t + 0.6);
  }
}

// ---------- Judging and delivering ----------

/** A struck note, as of a marimba: the fundamental, and its fourth partial dying sooner. */
function mallet(
  m: Mix,
  t: number,
  f: number,
  peak: number,
  decay = 0.7,
  out: AudioNode = m.effects,
): void {
  tone(m, t, { from: f, peak, attack: 0.003, decay, out });
  tone(m, t, { from: f * 4, peak: peak * 0.25, attack: 0.002, decay: 0.12, out });
}

/** きらりん: a few high twinkles. */
function sparkle(m: Mix, t: number): void {
  [96, 101, 105, 108, 103].forEach((n, k) => {
    tone(m, t + k * 0.06, { from: hz(n), peak: 0.07, attack: 0.002, decay: 0.35 });
  });
}

/** Each rank's jingle (MIDI notes), rising the further the better. */
const JINGLES: Record<string, number[]> = {
  SS: [77, 81, 84, 89, 93, 96],
  S: [77, 81, 84, 89],
  A: [77, 81, 84],
  B: [81, 84],
  C: [77, 81],
  D: [72, 77],
};

/** The judged piece's rank, rung out (a piece kneaded freely, with none, gets A's). */
function jingle(m: Mix, t: number, rank: string | null): void {
  const notes = JINGLES[rank ?? "A"] ?? JINGLES.A;
  notes.forEach((n, k) => mallet(m, t + k * 0.09, hz(n), 0.3));
  if (rank === "SS" || rank === "S") sparkle(m, t + notes.length * 0.09);
}

/**
 * ごおっ, ぱちぱち: the kiln's fire from `t`, roaring up and crackling, until it is put out
 * (by the function returned, or else six seconds on).
 */
function kiln(m: Mix, t: number): (at: number) => void {
  const { ctx } = m, most = 6;
  const out = new GainNode(ctx, { gain: 1 });
  out.connect(m.effects);
  // The fire catching.
  tone(m, t, { from: 55, to: 110, glide: 0.5, peak: 0.3, attack: 0.15, decay: 0.8, out });
  const swell = new GainNode(ctx, { gain: 0 });
  swell.gain.setValueAtTime(0, t);
  swell.gain.linearRampToValueAtTime(0.4, t + 0.6);
  swell.gain.setValueAtTime(0.4, t + most - 0.5);
  swell.gain.linearRampToValueAtTime(0, t + most);
  swell.connect(out);
  // Its roar, fluttering, and its crackle.
  const roar = new AudioBufferSourceNode(ctx, { buffer: m.noise, loop: true });
  roar.connect(new BiquadFilterNode(ctx, { type: "lowpass", frequency: 380, Q: 0.7 })).connect(
    swell,
  );
  const flutter = new OscillatorNode(ctx, { frequency: 4 });
  flutter.connect(new GainNode(ctx, { gain: 0.08 })).connect(swell.gain);
  const crackle = new AudioBufferSourceNode(ctx, { buffer: m.crackle, loop: true });
  crackle.connect(new BiquadFilterNode(ctx, { type: "highpass", frequency: 1200 })).connect(swell);
  const sources = [roar, flutter, crackle];
  for (const s of sources) {
    s.start(t);
    s.stop(t + most);
  }
  return (at) => {
    out.gain.setTargetAtTime(0, at, 0.2);
    for (const s of sources) s.stop(at + 1.2);
  };
}

/** チーン: the kiln's bell, the piece done. */
function bell(m: Mix, t: number): void {
  const f = hz(88);
  for (
    const [ratio, peak, decay] of [[1, 0.22, 1.6], [2.76, 0.1, 0.7], [5.4, 0.05, 0.35], [
      8.9,
      0.02,
      0.2,
    ]]
  ) {
    tone(m, t, { from: f * ratio, peak, attack: 0.002, decay });
  }
}

/** しゅっ: the iris closing (as it does, in 0.8 s), or opening (0.9 s) with a twinkle. */
function iris(m: Mix, t: number, closing: boolean): void {
  if (closing) {
    hiss(m, t, {
      filter: "bandpass",
      from: 500,
      to: 3600,
      q: 1.6,
      peak: 0.22,
      attack: 0.72,
      decay: 0.08,
    });
    tone(m, t + 0.78, { from: 220, to: 110, glide: 0.08, peak: 0.3, attack: 0.003, decay: 0.12 });
  } else {
    hiss(m, t, {
      filter: "bandpass",
      from: 3600,
      to: 600,
      q: 1.6,
      peak: 0.36,
      attack: 0.06,
      decay: 0.8,
    });
    sparkle(m, t + 0.55);
  }
}

/** さっ: the slip sliding in. */
function slip(m: Mix, t: number): void {
  hiss(m, t, {
    filter: "bandpass",
    from: 1800,
    to: 4200,
    q: 0.9,
    peak: 0.25,
    attack: 0.12,
    decay: 0.3,
  });
}

/** ぽん: the shop's stamp coming down on the slip. */
function stamp(m: Mix, t: number): void {
  tone(m, t, { from: 250, to: 85, glide: 0.07, peak: 0.7, attack: 0.002, decay: 0.16 });
  tone(m, t, { from: 540, to: 380, glide: 0.04, peak: 0.14, attack: 0.002, decay: 0.06 });
  hiss(m, t, { filter: "lowpass", from: 2000, peak: 0.3, attack: 0.001, decay: 0.03 });
}

/**
 * キラリン☆: the stamped slip shining. A glockenspiel runs up F major's pentatonic scale from
 * side to side (キラ), rings out on a shimmering bell (リン), and glitter scatters round it.
 */
function kirarin(m: Mix, t: number): void {
  [84, 86, 89, 91, 93, 96, 98].forEach((n, k) => {
    const f = hz(n), out = side(m, k % 2 ? 0.4 : -0.4, m.effects), at = t + k * 0.03;
    tone(m, at, { from: f, peak: 0.09, attack: 0.002, decay: 0.3, out });
    tone(m, at, { from: f * 2.76, peak: 0.02, attack: 0.002, decay: 0.1, out });
  });
  // Two bells a hair apart, beating a few times a second, over the octave below.
  const ring = t + 0.24, f = hz(101);
  for (const detune of [1, 1.002]) {
    tone(m, ring, { from: f * detune, peak: 0.1, attack: 0.003, decay: 1.2 });
  }
  tone(m, ring, { from: f / 2, peak: 0.06, attack: 0.003, decay: 0.9 });
  tone(m, ring, { from: (f / 2) * 2.76, peak: 0.015, attack: 0.002, decay: 0.3 });
  hiss(m, ring, { filter: "highpass", from: 8000, peak: 0.05, attack: 0.08, decay: 0.7 });
  for (let k = 0; k < 7; k++) {
    const at = ring + 0.06 + k * 0.12 + Math.random() * 0.05;
    tone(m, at, {
      from: hz(103 + Math.random() * 10),
      peak: 0.025 + Math.random() * 0.02,
      attack: 0.002,
      decay: 0.12,
      out: side(m, Math.random() * 1.2 - 0.6, m.effects),
    });
  }
}

/** ことっ: the piece set down on the shelf. */
function knock(m: Mix, t: number): void {
  for (const [at, f, peak] of [[0, 820, 0.4], [0.08, 600, 0.3]]) {
    tone(m, t + at, { from: f, to: f * 0.9, peak, attack: 0.002, decay: 0.06 });
    hiss(m, t + at, {
      filter: "bandpass",
      from: f * 1.8,
      q: 5,
      peak: peak * 0.5,
      attack: 0.001,
      decay: 0.03,
    });
  }
}

/** かしゃっ: the commemorative photo taken. */
function shutter(m: Mix, t: number): void {
  hiss(m, t, { filter: "highpass", from: 2500, peak: 0.38, attack: 0.001, decay: 0.025 });
  tone(m, t, { type: "square", from: 1500, peak: 0.04, attack: 0.001, decay: 0.012 });
  hiss(m, t + 0.07, {
    filter: "bandpass",
    from: 1800,
    q: 1.5,
    peak: 0.38,
    attack: 0.002,
    decay: 0.07,
  });
}

/** ぽこ: a button pressed. */
function tap(m: Mix, t: number): void {
  const f = vary(900, 0.03);
  tone(m, t, { from: f, to: f * 0.7, glide: 0.03, peak: 0.15, attack: 0.002, decay: 0.05 });
}

// ---------- The kitten ----------

/** A breath of the purr: in, then (a little louder, a little faster) out. */
const BREATH = 1.5;

/** ごろごろ: the kitten purring for `seconds`, some 25 pulses a second, breathing in and out. */
function purr(m: Mix, t: number, seconds: number): void {
  const { ctx } = m, end = t + seconds;
  const source = new AudioBufferSourceNode(ctx, { buffer: m.noise, loop: true });
  const pulse = new GainNode(ctx, { gain: 0.5 });
  const beat = new OscillatorNode(ctx, { frequency: 23 });
  beat.connect(new GainNode(ctx, { gain: 0.5 })).connect(pulse.gain);
  for (let at = 0; at < seconds; at += BREATH) {
    beat.frequency.setValueAtTime(23, t + at);
    beat.frequency.setValueAtTime(26, t + at + BREATH / 2);
  }
  const curve = new Float32Array(Math.ceil(seconds * 60));
  for (let i = 0; i < curve.length; i++) {
    const s = (i / (curve.length - 1)) * seconds, phase = (s % BREATH) / BREATH;
    const half = phase < 0.5 ? phase * 2 : phase * 2 - 1;
    const shape = half < 0.88 ? Math.sin((Math.PI * half) / 0.88) ** 0.6 : 0;
    const fade = Math.min(1, s / 0.15, (seconds - s) / 0.3);
    curve[i] = 1.6 * (phase < 0.5 ? 0.7 : 1) * (0.15 + 0.85 * shape) * fade;
  }
  const breath = new GainNode(ctx, { gain: 0 });
  breath.gain.setValueCurveAtTime(curve, t, seconds);
  source.connect(new BiquadFilterNode(ctx, { type: "lowpass", frequency: 450, Q: 0.9 }))
    .connect(pulse)
    .connect(breath)
    .connect(m.effects);
  source.start(t, Math.random() * m.noise.duration);
  beat.start(t);
  source.stop(end);
  beat.stop(end);
}

/**
 * How each mew goes: its length (s), and its pitch, its mouth (the two lowest formants, from
 * the closed "n" through the open "a" to the rounded "u") and its loudness, evenly spaced over it.
 */
const MEWS = {
  /** にゃっ */
  short: {
    seconds: 0.28,
    pitch: [560, 780, 720, 540],
    f1: [420, 950, 820, 600],
    f2: [2100, 1700, 1500, 1250],
    loud: [0, 1, 0.85, 0],
  },
  /** にゃーん */
  long: {
    seconds: 0.7,
    pitch: [520, 800, 830, 740, 620, 470],
    f1: [400, 920, 1000, 900, 720, 480],
    f2: [2200, 1750, 1600, 1400, 1150, 900],
    loud: [0, 0.9, 1, 0.95, 0.7, 0],
  },
  /** にゃ？ */
  ask: {
    seconds: 0.45,
    pitch: [520, 740, 650, 700, 920],
    f1: [420, 950, 880, 800, 700],
    f2: [2100, 1700, 1600, 1800, 2100],
    loud: [0, 1, 0.8, 0.9, 0],
  },
};

/** A mew: a buzz shaped by a moving mouth, wavering a little. */
function mew(m: Mix, t: number, kind: keyof typeof MEWS): void {
  const { ctx } = m, { seconds, pitch, f1, f2, loud } = MEWS[kind], k = vary(1, 0.06);
  const curve = (xs: number[], by = 1) => Float32Array.from(xs, (x) => x * by);
  const voice = new OscillatorNode(ctx, { type: "sawtooth" });
  voice.frequency.setValueCurveAtTime(curve(pitch, k), t, seconds);
  const waver = new OscillatorNode(ctx, { frequency: 7 });
  waver.connect(new GainNode(ctx, { gain: 10 })).connect(voice.frequency);
  const soft = new BiquadFilterNode(ctx, { type: "lowpass", frequency: 4000 });
  voice.connect(soft);
  const out = new GainNode(ctx, { gain: 0 });
  out.gain.setValueCurveAtTime(curve(loud, 0.7), t, seconds);
  out.connect(m.effects);
  for (const [f, q, gain] of [[f1, 6, 1], [f2, 9, 0.5], [[3000, 3000], 5, 0.12]] as const) {
    const formant = new BiquadFilterNode(ctx, { type: "bandpass", Q: q });
    formant.frequency.setValueCurveAtTime(curve([...f]), t, seconds);
    soft.connect(formant).connect(new GainNode(ctx, { gain })).connect(out);
  }
  for (const o of [voice, waver]) {
    o.start(t);
    o.stop(t + seconds + 0.05);
  }
}

/** What the kitten on the title screen can say when poked. */
export type KittenSound = "mew" | "meow" | "ask" | "squish" | "knead" | "purr";

function kitten(m: Mix, t: number, say: KittenSound): void {
  if (say === "squish") squish(m, t, "push", 0.8);
  else if (say === "knead") {
    squish(m, t, "push", 0.6);
    squish(m, t + 0.28, "push", 0.6);
  } else if (say === "purr") purr(m, t, 1.6);
  else mew(m, t, say === "mew" ? "short" : say === "meow" ? "long" : "ask");
}

// ---------- The workshop's music ----------

/** A tune, round and round: its bar's length (s), and how bar `n` is laid down from `t`. */
interface Tune {
  length: number;
  bar(m: Mix, t: number, n: number): void;
}

// ほんわか: the waltz in the workshop, the shuffle at the clay.

/** Each chord's bass (the root, then the fifth) and the notes strummed (MIDI). */
const COZY_CHORDS: Record<string, { bass: [number, number]; strum: number[] }> = {
  F: { bass: [41, 48], strum: [57, 60, 65] },
  Am: { bass: [45, 52], strum: [57, 60, 64] },
  Bb: { bass: [46, 53], strum: [58, 62, 65] },
  C: { bass: [48, 43], strum: [55, 60, 64] },
  C7: { bass: [48, 43], strum: [58, 64, 67] },
  Dm: { bass: [50, 45], strum: [57, 62, 65] },
  E7: { bass: [40, 47], strum: [56, 62, 64] },
  G: { bass: [43, 50], strum: [55, 59, 62] },
  G7: { bass: [43, 50], strum: [53, 59, 62] },
  Gm7: { bass: [43, 50], strum: [58, 62, 65] },
};

/** The shuffle's beat (s), at 92 a minute; each bar is four. */
const SHUFFLE_BEAT = 60 / 92;
/** How far into its beat an offbeat eighth falls: gently swung. */
const SWING = 0.62;
/**
 * The shuffle, 16 bars in F: each bar's chords (one, or two of half a bar each) and its notes,
 * as [beat, MIDI note, beats long].
 */
const SHUFFLE_BARS: { chords: string[]; notes: [number, number, number][] }[] = [
  {
    chords: ["F"],
    notes: [[0, 69, 1], [1, 72, 0.5], [1.5, 77, 0.5], [2, 76, 1.5], [3.5, 74, 0.5]],
  },
  { chords: ["Am"], notes: [[0, 72, 1], [1, 69, 0.5], [1.5, 72, 0.5], [2, 76, 2]] },
  { chords: ["Bb"], notes: [[0, 74, 1], [1, 70, 0.5], [1.5, 74, 0.5], [2, 77, 2]] },
  {
    chords: ["C7"],
    notes: [[0, 74, 0.5], [0.5, 72, 0.5], [1, 70, 0.5], [1.5, 69, 0.5], [2, 67, 2]],
  },
  {
    chords: ["F"],
    notes: [[0, 69, 1], [1, 72, 0.5], [1.5, 77, 0.5], [2, 81, 1.5], [3.5, 79, 0.5]],
  },
  { chords: ["Dm"], notes: [[0, 77, 1], [1, 74, 0.5], [1.5, 77, 0.5], [2, 81, 2]] },
  {
    chords: ["Gm7"],
    notes: [[0, 79, 1], [1, 77, 0.5], [1.5, 74, 0.5], [2, 70, 1.5], [3.5, 72, 0.5]],
  },
  { chords: ["C7"], notes: [[0, 76, 1], [1, 74, 0.5], [1.5, 72, 0.5], [2, 70, 1], [3, 67, 1]] },
  { chords: ["Bb"], notes: [[0, 74, 1.5], [1.5, 72, 0.5], [2, 74, 1], [3, 77, 1]] },
  { chords: ["C"], notes: [[0, 76, 1.5], [1.5, 74, 0.5], [2, 76, 1], [3, 79, 1]] },
  { chords: ["Am"], notes: [[0, 81, 1], [1, 79, 0.5], [1.5, 76, 0.5], [2, 72, 1], [3, 76, 1]] },
  { chords: ["Dm"], notes: [[0, 74, 1], [1, 77, 0.5], [1.5, 76, 0.5], [2, 74, 2]] },
  {
    chords: ["Gm7"],
    notes: [[0, 70, 1], [1, 74, 0.5], [1.5, 77, 0.5], [2, 79, 1], [3, 77, 0.5], [3.5, 74, 0.5]],
  },
  { chords: ["C7"], notes: [[0, 76, 1], [1, 79, 0.5], [1.5, 76, 0.5], [2, 72, 1], [3, 70, 1]] },
  {
    chords: ["F"],
    notes: [[0, 69, 0.5], [0.5, 72, 0.5], [1, 77, 1.5], [2.5, 79, 0.5], [3, 81, 1]],
  },
  { chords: ["F", "C7"], notes: [[0, 77, 2]] },
];

/** A plucked string, as of a ukulele (or, softer on a triangle, a harp): bright, soon mellow. */
function pluck(
  m: Mix,
  t: number,
  f: number,
  peak: number,
  out: AudioNode,
  type: OscillatorType = "sawtooth",
): void {
  const { ctx } = m;
  const string = new OscillatorNode(ctx, { type, frequency: f });
  const mellow = new BiquadFilterNode(ctx, { type: "lowpass", Q: 1.5 });
  mellow.frequency.setValueAtTime(3000, t);
  mellow.frequency.exponentialRampToValueAtTime(500, t + 0.15);
  string.connect(mellow).connect(envelope(m, t, peak, 0.003, 0.4)).connect(out);
  string.start(t);
  string.stop(t + 0.45);
}

/** A chord strummed down: its notes plucked from the lowest up, a moment apart. */
function strum(
  m: Mix,
  t: number,
  notes: number[],
  peak: number,
  out: AudioNode,
  type?: OscillatorType,
): void {
  notes.forEach((n, k) => pluck(m, t + k * 0.012, hz(n), peak, out, type));
}

/** Into the music (or `into`) a little to one side (-1 left, 1 right). */
function side(m: Mix, pan: number, into: AudioNode = m.music): AudioNode {
  const panner = new StereoPannerNode(m.ctx, { pan });
  panner.connect(into);
  return panner;
}

/**
 * Bar `n` of the shuffle, from `t`: bass and strum going oom-pah under a marimba's tune, a
 * shaker on every eighth.
 */
function shuffleBar(m: Mix, t: number, n: number): void {
  const k = n % SHUFFLE_BARS.length, { chords, notes } = SHUFFLE_BARS[k];
  const at = (beat: number) => {
    const whole = Math.floor(beat);
    return t + (whole + (beat > whole ? SWING : 0)) * SHUFFLE_BEAT;
  };
  const chordAt = (beat: number) => COZY_CHORDS[chords[beat >= 2 ? chords.length - 1 : 0]];
  // The strum a little to the left, the tune a little to the right.
  const left = side(m, -0.3), right = side(m, 0.15);
  // Oom-pah: the bass on the first and third beats (the root, then the fifth, or the next
  // chord's root), the chord strummed on the second and fourth, and softly after the fourth.
  for (const beat of [0, 2]) {
    const { bass } = chordAt(beat), next = beat === 2 && chords.length > 1;
    tone(m, at(beat), {
      type: "triangle",
      from: hz(beat === 0 || next ? bass[0] : bass[1]),
      peak: 0.35,
      attack: 0.008,
      decay: 0.45,
      cut: 700,
      out: m.music,
    });
  }
  for (const [beat, peak] of [[1, 0.16], [3, 0.16], [3.5, 0.1]]) {
    strum(m, at(beat), chordAt(beat).strum, peak, left);
  }
  // A shaker on every eighth, the offbeats a little louder.
  for (let eighth = 0; eighth < 8; eighth++) {
    hiss(m, at(eighth / 2), {
      filter: "highpass",
      from: 6500,
      peak: eighth % 2 ? 0.1 : 0.055,
      attack: 0.004,
      decay: 0.045,
      out: m.music,
    });
  }
  // The tune, echoed in the second eight bars an octave up on a glockenspiel.
  for (const [beat, note, beats] of notes) {
    const when = at(beat) + Math.random() * 0.012, f = hz(note);
    mallet(m, when, f, 0.3, Math.min(1.1, 0.3 + 0.35 * beats), right);
    if (k >= 8) tone(m, when, { from: f * 2, peak: 0.08, attack: 0.002, decay: 0.6, out: right });
  }
}

/** The waltz's beat (s), at 126 a minute; each bar is three. */
const WALTZ_BEAT = 60 / 126;
/** The waltz, 16 bars in C: each bar's chord and its notes, as [beat, MIDI note, beats long]. */
const WALTZ_BARS: { chord: string; notes: [number, number, number][] }[] = [
  { chord: "C", notes: [[0, 67, 1], [1, 72, 1], [2, 76, 1]] },
  { chord: "C", notes: [[0, 79, 2], [2, 76, 1]] },
  { chord: "Am", notes: [[0, 69, 1], [1, 72, 1], [2, 76, 1]] },
  { chord: "Am", notes: [[0, 81, 2], [2, 79, 1]] },
  { chord: "F", notes: [[0, 77, 1], [1, 76, 1], [2, 77, 1]] },
  { chord: "F", notes: [[0, 81, 2], [2, 77, 1]] },
  { chord: "G", notes: [[0, 74, 1], [1, 71, 1], [2, 74, 1]] },
  { chord: "G7", notes: [[0, 79, 2], [2, 77, 1]] },
  { chord: "C", notes: [[0, 76, 1], [1, 72, 1], [2, 76, 1]] },
  { chord: "E7", notes: [[0, 74, 2], [2, 71, 1]] },
  { chord: "Am", notes: [[0, 72, 1], [1, 69, 1], [2, 72, 1]] },
  { chord: "C7", notes: [[0, 76, 1], [1, 79, 1], [2, 82, 1]] },
  { chord: "F", notes: [[0, 81, 2], [2, 77, 1]] },
  { chord: "G7", notes: [[0, 79, 1], [1, 77, 1], [2, 74, 1]] },
  { chord: "C", notes: [[0, 72, 3]] },
  { chord: "C", notes: [] },
];

/**
 * A held note, as of an ocarina: nearly a pure tone, breathed in with a little chiff, and
 * wavering the longer it is held.
 */
function ocarina(
  m: Mix,
  t: number,
  f: number,
  seconds: number,
  peak: number,
  out: AudioNode,
): void {
  const { ctx } = m;
  const wave = new PeriodicWave(ctx, {
    real: new Float32Array(4),
    imag: Float32Array.from([0, 1, 0.12, 0.05]),
  });
  const voice = new OscillatorNode(ctx, { type: "custom", periodicWave: wave, frequency: f });
  const waver = new OscillatorNode(ctx, { frequency: 5 });
  const depth = new GainNode(ctx, { gain: 0 });
  depth.gain.setValueAtTime(0, t);
  depth.gain.linearRampToValueAtTime(f * 0.006, t + Math.min(0.5, seconds));
  waver.connect(depth).connect(voice.frequency);
  const breath = new GainNode(ctx, { gain: 0 });
  breath.gain.setValueAtTime(0, t);
  breath.gain.linearRampToValueAtTime(peak, t + 0.04);
  breath.gain.setTargetAtTime(peak * 0.8, t + 0.04, 0.15);
  breath.gain.setTargetAtTime(0, t + seconds * 0.9, 0.05);
  voice.connect(breath).connect(out);
  hiss(m, t, {
    filter: "bandpass",
    from: f * 3,
    q: 2,
    peak: peak * 0.15,
    attack: 0.01,
    decay: 0.05,
    out,
  });
  for (const o of [voice, waver]) {
    o.start(t);
    o.stop(t + seconds + 0.4);
  }
}

/**
 * Bar `n` of the waltz, from `t`: oom-pa-pa on bass and harp under an ocarina's tune, played
 * twice through, a music box echoing it the second time.
 */
function waltzBar(m: Mix, t: number, n: number): void {
  const k = n % (2 * WALTZ_BARS.length),
    i = k % WALTZ_BARS.length,
    { chord, notes } = WALTZ_BARS[i];
  const { bass, strum: harp } = COZY_CHORDS[chord];
  const left = side(m, -0.25), right = side(m, 0.2);
  // Oom-pa-pa: the bass on the first beat (the root, or the fifth when the chord goes on from
  // the bar before), the chord on the second and third.
  const again = i > 0 && WALTZ_BARS[i - 1].chord === chord;
  tone(m, t, {
    type: "triangle",
    from: hz(bass[again ? 1 : 0]),
    peak: 0.3,
    attack: 0.008,
    decay: 0.6,
    cut: 700,
    out: m.music,
  });
  for (const beat of [1, 2]) strum(m, t + beat * WALTZ_BEAT, harp, 0.13, left, "triangle");
  for (const [beat, note, beats] of notes) {
    const when = t + beat * WALTZ_BEAT, f = hz(note);
    ocarina(m, when, f, beats * WALTZ_BEAT, 0.1, right);
    if (k >= WALTZ_BARS.length) {
      tone(m, when, { from: f * 2, peak: 0.08, attack: 0.002, decay: 0.6, out: right });
    }
  }
}

// Quiet, among the pieces kept and in the ledger: a pad, the bass, and a tune wandering on an
// electric piano.

/** A beat (s), at 66 a minute; each bar is four. */
const QUIET_BEAT = 60 / 66;
/** The chords, a bar each, round and round (MIDI notes): Fmaj7, Am7, B♭maj7, C9sus4. */
const QUIET_CHORDS = [
  { bass: 41, pad: [53, 57, 60, 64] },
  { bass: 45, pad: [57, 60, 64, 67] },
  { bass: 46, pad: [58, 62, 65, 69] },
  { bass: 48, pad: [58, 62, 65, 67] },
];
/** The tune's notes: F major's pentatonic scale, from F4 up to D6. */
const SCALE = [65, 67, 69, 72, 74, 77, 79, 81, 84, 86];
/** Where a bar's notes fall, in eighths. */
const RHYTHMS = [[0, 4], [0, 2, 4], [0, 3, 6], [2, 4, 6], [0, 4, 6], [0, 2, 4, 6], [0, 6]];
/** Where the tune is on the scale: it wanders a step or two at a time. */
let wander = 3;

/** A soft electric piano's note: a sine bent by another at its pitch, mellowing as it dies. */
function epiano(m: Mix, t: number, f: number, peak: number): void {
  const { ctx } = m;
  const carrier = new OscillatorNode(ctx, { frequency: f });
  const bend = new OscillatorNode(ctx, { frequency: f });
  const depth = new GainNode(ctx, { gain: 0 });
  depth.gain.setValueAtTime(f * 1.2, t);
  depth.gain.exponentialRampToValueAtTime(f * 0.05, t + 1);
  bend.connect(depth).connect(carrier.frequency);
  carrier.connect(envelope(m, t, peak, 0.004, 1.8))
    .connect(new StereoPannerNode(ctx, { pan: (Math.random() * 2 - 1) * 0.35 }))
    .connect(m.music);
  for (const o of [carrier, bend]) {
    o.start(t);
    o.stop(t + 1.9);
  }
}

/** The scale step nearest `step` on one of the chord's notes. */
function onChord(step: number, notes: number[]): number {
  const classes = new Set(notes.map((n) => n % 12));
  for (let d = 0; d < SCALE.length; d++) {
    for (const s of [step - d, step + d]) {
      if (s >= 0 && s < SCALE.length && classes.has(SCALE[s] % 12)) return s;
    }
  }
  return step;
}

/** Bar `n` of the quiet tune, from `t`: its chord swelling on a pad, the bass, and the tune. */
function quietBar(m: Mix, t: number, n: number): void {
  const { ctx } = m, { bass, pad } = QUIET_CHORDS[n % QUIET_CHORDS.length];
  const length = 4 * QUIET_BEAT;
  // The pad swells in and dies away into the next bar's.
  const swell = new GainNode(ctx, { gain: 0 });
  swell.gain.setValueAtTime(0, t);
  swell.gain.linearRampToValueAtTime(0.03, t + 1.2);
  swell.gain.setValueAtTime(0.03, t + length);
  swell.gain.linearRampToValueAtTime(0, t + length + 1.4);
  const dull = new BiquadFilterNode(ctx, { type: "lowpass", frequency: 1000 });
  dull.connect(swell).connect(m.music);
  for (const note of pad) {
    for (const detune of [-6, 6]) {
      const osc = new OscillatorNode(ctx, { type: "triangle", frequency: hz(note), detune });
      osc.connect(dull);
      osc.start(t);
      osc.stop(t + length + 1.5);
    }
  }
  for (const [beat, peak] of [[0, 0.12], [2, 0.08]]) {
    tone(m, t + beat * QUIET_BEAT, {
      type: "triangle",
      from: hz(bass),
      peak,
      attack: 0.02,
      decay: 2 * QUIET_BEAT,
      cut: 400,
      out: m.music,
    });
  }
  // The tune: sparser at the end of each four bars, on a chord note at the start of a bar.
  const rhythm = n % 8 === 7 ? [0] : n % 4 === 3 ? pick([[0], [0, 4]]) : pick(RHYTHMS);
  for (const eighth of rhythm) {
    wander = Math.min(SCALE.length - 1, Math.max(0, wander + pick([-2, -1, -1, 0, 1, 1, 2])));
    if (eighth === 0) wander = onChord(wander, [bass, ...pad]);
    const at = t + (eighth * QUIET_BEAT) / 2 + Math.random() * 0.02;
    epiano(m, at, hz(SCALE[wander]), (eighth === 0 ? 0.12 : 0.1) * vary(1, 0.2));
  }
}

/** The workshop's tunes, one to each part of it (see main.ts). */
export type TuneName = "waltz" | "shuffle" | "quiet";
const TUNES: Record<TuneName, Tune> = {
  waltz: { length: 3 * WALTZ_BEAT, bar: waltzBar },
  shuffle: { length: 4 * SHUFFLE_BEAT, bar: shuffleBar },
  quiet: { length: 4 * QUIET_BEAT, bar: quietBar },
};

// ---------- Playing ----------

let ctx: AudioContext | null = null;
let live: Mix | null = null;
const volumes = { effects: 0.8, music: 0.5 };
/** The tune played, and its own gain (faded out when another takes over). */
let tune: TuneName = "waltz";
let tuneOut: GainNode | null = null;
/** When the tune's next bar starts (on the context's clock), and which bar it is. */
let barAt = 0, bars = 0;

/** How loud the effects and the music are (0 to 1, as the settings' sliders say). */
export function setVolumes(effects: number, music: number): void {
  volumes.effects = effects;
  volumes.music = music;
  if (live) heed(live);
}

/** Heard as growing evenly louder: the square of the slider. */
function heed(m: Mix): void {
  m.effects.gain.setValueAtTime(volumes.effects ** 2, m.ctx.currentTime);
  // Eased, as the music may be playing while the slider moves.
  m.music.gain.setTargetAtTime(volumes.music ** 2, m.ctx.currentTime, 0.05);
}

/** Starts the sound, as the player leaves the start page by a touch (see ui/start.tsx). */
export function startSound(): void {
  if (!ctx) {
    ctx = new AudioContext();
    live = mixOn(ctx);
    heed(live);
    startTune(ctx, live);
    setInterval(playMusic, 200);
  }
  wake();
}

/** Lets the sound go on, should the browser have held it back till the next touch. */
function wake(): void {
  if (ctx && ctx.state !== "running" && !document.hidden) void ctx.resume();
}
for (const type of ["pointerdown", "pointerup", "keydown"]) {
  window.addEventListener(type, wake, true);
}
document.addEventListener("visibilitychange", () => {
  if (ctx) void (document.hidden ? ctx.suspend() : ctx.resume());
});
// Every button and switch taps as it is pressed.
document.addEventListener("click", (e) => {
  if (e.target instanceof Element && e.target.closest("button, input[type=checkbox]")) {
    sounds.tap();
  }
}, true);

/** Plays the tune `name` from its start, the one playing before fading out (see TUNES). */
export function setTune(name: TuneName): void {
  if (name === tune) return;
  tune = name;
  if (ctx && live) startTune(ctx, live);
}

function startTune(ctx: AudioContext, m: Mix): void {
  tuneOut?.gain.setTargetAtTime(0, ctx.currentTime, 0.4);
  tuneOut = new GainNode(ctx);
  tuneOut.connect(m.music);
  barAt = ctx.currentTime + 0.6;
  bars = 0;
}

/** Lays down the tune's bars as they come near (none while it is turned all the way down). */
function playMusic(): void {
  if (!ctx || !live || !tuneOut || ctx.state !== "running") return;
  const { length, bar } = TUNES[tune], m = { ...live, music: tuneOut };
  // Late (a timer held back), it starts again from now rather than catching up all at once.
  if (barAt < ctx.currentTime) barAt = ctx.currentTime + 0.1;
  while (barAt < ctx.currentTime + 0.5) {
    if (volumes.music > 0) bar(m, barAt, bars);
    barAt += length;
    bars++;
  }
}

/** Plays `voice` now (or `delay` seconds on), once there is sound, and it is not turned off. */
function play(voice: (m: Mix, t: number) => void, delay = 0): void {
  if (!live || document.hidden || volumes.effects === 0) return;
  voice(live, live.ctx.currentTime + delay);
}

/** When the brush last squished on its way (ms). */
let kneadedAt = 0;

/** The game's sounds, played as things happen. */
export const sounds = {
  /** A stroke begun with `tool`. */
  press: (tool: Tool) => play((m, t) => squish(m, t, tool, 1)),
  /** The brush moved on some way with `tool`: a softer squish, not too often. */
  knead(tool: Tool) {
    const now = performance.now();
    if (now - kneadedAt < 70) return;
    kneadedAt = now;
    play((m, t) => squish(m, t, tool, 0.3));
  },
  /** A stroke counted: the paw print pressed on. */
  counted: () => play(paw),
  undo: () => play(rewind),
  restart: () => play(restart),
  wobble: () => play(wobble),
  /** The piece judged, `rank` (null: kneaded freely, there is none). */
  jingle: (rank: string | null) => play((m, t) => jingle(m, t, rank)),
  /** The kiln's fire lit; the function returned puts it out. */
  kiln(): () => void {
    if (!live || document.hidden || volumes.effects === 0) return () => {};
    const out = kiln(live, live.ctx.currentTime), m = live;
    return () => out(m.ctx.currentTime);
  },
  bell: () => play(bell),
  iris: (closing: boolean) => play((m, t) => iris(m, t, closing)),
  slip: () => play(slip),
  /** The stamp's "ぽん", `delay` seconds on (as it comes down onto the slip). */
  stamp: (delay: number) => play(stamp, delay),
  /** キラリン☆, `delay` seconds on (once the slip is stamped). */
  kirarin: (delay: number) => play(kirarin, delay),
  keep: () => play(knock),
  shutter: () => play(shutter),
  tap: () => play(tap),
  kitten: (say: KittenSound) => play((m, t) => kitten(m, t, say)),
};
